import type { Dict } from '@chakra-ui/utils';
import { ComponentState } from 'ts/common/constants/theme/common';
import { colors } from 'ts/studio/colors';
import { alertTheme } from 'ts/studio/themes/alertTheme';
import { buttonTheme } from 'ts/studio/themes/buttonTheme';
import { radioTheme } from 'ts/studio/themes/radioTheme';
import { stepperTheme } from 'ts/studio/themes/stepperTheme';
import { switchTheme } from 'ts/studio/themes/switchTheme';
import { tableTheme } from 'ts/studio/themes/tableTheme';
import { tagTheme } from 'ts/studio/themes/tagTheme';

type ResponsiveStyleParam = TemplateStringsArray | string;

export function convertHexToRgba(hex: string, alpha = 1): string {
    const [, r1, r2, g1, g2, b1, b2] = Array.from(hex);
    const blue = parseInt(`${b1}${b2}`, 16);
    const green = parseInt(`${g1}${g2}`, 16);
    const red = parseInt(`${r1}${r2}`, 16);

    if (alpha > 1) {
        alpha = 1;
    } else if (alpha < 0) {
        alpha = 0;
    }

    return `rgba(${red},${green},${blue},${alpha})`;
}

/**
 * @deprecated Use colors.primary[300] instead
 */
export const navy300 = '#1280BE';

/**
 * @deprecated Use colors.primary[400] instead
 */
export const navy600 = '#0C547D'; // This is the "primary" color used for Primary Buttons, for example.

/**
 * @deprecated Use colors.primary[600] instead
 */
export const navy700 = '#002B4C';

/**
 * @deprecated Use colors.neutral[300] instead
 */
export const gray300 = '#BCC8CD';

/**
 * @deprecated Use colors.secondary[400] instead
 */
export const blue400 = '#49BDE4';

const breakpoints = {
    base: '0',
    sm: '480',
    md: '768',
    lg: '1200',
    xl: '1900',
    '2xl': '2400'
};

const { md, lg, xl } = breakpoints;
const responsive = {
    extraLargeScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(xl) + 1}px) {
      ${rules}
  }
`,
    largeScreenDown: (rules: ResponsiveStyleParam) => `
  @media only screen and (max-width: ${xl}px) {
      ${rules}
  }
`,
    largeScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(lg) + 1}px) and (max-width: ${xl}px) {
      ${rules}
  }
`,
    largeScreenUp: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(lg) + 1}px) {
      ${rules}
  }
`,
    mediumScreenDown: (rules: ResponsiveStyleParam) => `
  @media only screen and (max-width: ${lg}px) {
      ${rules}
  }
`,
    mediumScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(md) + 1}px) and (max-width: ${lg}px) {
      ${rules}
  }
`,
    mediumScreenUp: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(md) + 1}px) {
      ${rules}
  }
`,
    smallScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (max-width: ${md}px) {
      ${rules}
  }
`
};

const fonts = {
    body: 'azo-sans-web, Helvetica, Arial, sans-serif',
    heading: 'azo-sans-web, Helvetica, Arial, sans-serif',
    mono: 'azo-sans-web, Helvetica, Arial, sans-serif'
};

const components = {
    Alert: alertTheme,
    Button: buttonTheme,
    Checkbox: {
        customStyle: {
            backgroundColor: colors.gray[100],
            borderColor: colors.gray[400],
            checkColor: colors.blue[400],
            checkWidth: '8px',
            width: '16px',
            [ComponentState.Disabled]: {
                backgroundColor: colors.gray[100],
                borderColor: colors.gray[200],
                checkColor: colors.blue[100]
            }
        }
    },
    DatePicker: {
        baseStyle: {
            fontFamily: fonts.heading,
            backgroundColor: navy600,
            headerBackgroundColor: 'white',
            color: colors.neutral[600],
            disabledColor: '#cccccc',
            borderColor: colors.gray[200],
            dayColor: colors.gray[700],
            dayDisabledColor: colors.gray[300],
            dayHeaderColor: colors.gray[500],
            monthColor: colors.gray[600],
            monthDisabledColor: colors.gray[300],
            navigationButtonColor: colors.gray[300],
            rangeBackgroundColor: colors.blue[100],
            rangeColor: colors.blue[700],
            selectedColor: colors.blue[300]
        }
    },
    Form: {
        baseStyle: {
            requiredIndicator: {
                color: 'inherit',
                margin: 0
            }
        }
    },
    FormErrorMessage: {
        baseStyle: {
            color: colors.red[300],
            fontWeight: 300
        }
    },
    FormHelperText: {
        baseStyle: {
            fontWeight: 300
        }
    },
    FormLabel: {
        baseStyle: {
            fontSize: 14,
            lineHeight: '25px',
            color: colors.navy[400]
        },
        sizes: {
            lg: {
                fontSize: 16
            }
        }
    },
    Input: {
        baseStyle: {
            fontFamily: fonts.heading,
            field: {
                fontSize: '14px'
            }
        },
        variants: {
            withAddon: {
                addon: {
                    background: 'gray.600',
                    backgroundColor: 'gray.600',
                    border: `1px solid`,
                    borderColor: 'gray.600',
                    borderRadius: '4px',
                    color: 'white',
                    fontSize: '16px',
                    height: 'inherit',
                    paddingLeft: '10px',
                    paddingRight: '10px'
                },
                field: {
                    border: '1px solid',
                    borderColor: 'gray.600',
                    borderRadius: '4px'
                }
            }
        }
    },
    Switch: switchTheme,
    Menu: {
        variants: {
            contextMenu: {
                button: {
                    height: '24px',
                    padding: 0,
                    right: 0,
                    top: 0,
                    width: '24px'
                },
                item: {
                    borderRadius: '8px',
                    color: 'primary.400',
                    display: 'flex',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    padding: '10px 12px'
                },
                list: {
                    borderRadius: '12px',
                    minW: 'unset',
                    padding: '10px',
                    width: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                }
            }
        }
    },
    Select: {
        baseStyle: {
            field: {
                fontSize: '14px'
            }
        }
    },
    Stepper: stepperTheme,
    Radio: radioTheme,
    Text: {
        baseStyle: {
            fontFamily: fonts.heading
        },
        variants: {
            error: {
                color: 'red.400'
            }
        }
    },
    Tooltip: {
        baseStyle: {
            background: 'primary.600',
            borderRadius: '8px',
            color: 'white',
            fontSize: '12px',
            lineHeight: '12px',
            fontWeight: 300,
            padding: '6px'
        },
        variants: {
            bold: {
                textTransform: 'uppercase',
                fontWeight: 500,
                padding: '8px',
                letterSpacing: '1.4px'
            },
            light: {
                '--popper-arrow-bg': 'white',
                background: 'white',
                color: 'gray.700'
            }
        }
    },
    Table: tableTheme,
    Tag: tagTheme
};

const getTheme = (): Dict => {
    return {
        colors,
        fonts,
        fontColor: {
            default: 'inherit'
        },
        styles: {
            global: {
                body: {
                    color: 'gray.600',
                    fontFamily: null,
                    lineHeight: null,
                    transition: null
                },
                input: {
                    fontFamily: fonts.body,
                    color: 'gray.600'
                },
                select: {
                    fontFamily: fonts.body,
                    color: 'gray.600'
                }
            }
        },
        components,
        responsive
    };
};

export default getTheme;
